import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type Store from '@ember-data/store'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { Changeset } from 'ember-changeset'
import type { BufferedChangeset } from 'ember-changeset/types'
import { didCancel, task } from 'ember-concurrency'
import type { Log } from '@blakeelearning/log'
import type { IntlService } from 'ember-intl'
import { assert } from '@ember/debug'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import type RouterService from '@ember/routing/router-service'
import { BookLibraryDateRange } from 'teacher-ui-client/components/book-library/date-range'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { LayoutBlocksConfirmModal } from 'teacher-ui-client/components/layout-blocks/confirm-modal'
import { fn } from '@ember/helper'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { or } from 'ember-truth-helpers'
import type BookLibrarySharesIndexByCollectionEditRoute from 'teacher-ui-client/subscription-type/book-library/shares/index/by-collection/edit/route'
import type Owner from '@ember/owner'

interface Args {
  model: ModelFor<BookLibrarySharesIndexByCollectionEditRoute>
}

interface Signature {
  Args: Args
  Element: HTMLFormElement
}

export class SharesByCollectionEdit extends Component<Signature> {
  @service store!: Store

  @service log!: Log

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service router!: RouterService

  @tracked shareChangeset!: BufferedChangeset

  @tracked showExpireConfirmModal = false

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    const share = this.store.peekRecord<BookLibraryShare>('book-library/share', this.args.model.shareId)

    assert('share for given share id not found in store', share)
    this.shareChangeset = Changeset(share)
  }

  get editStartDateDisabled() {
    return this.shareChangeset.status === ShareStatus.Active
  }

  get themeKey() {
    return subscriptionTypeToThemeKey(this.args.model.subscriptionType)
  }

  saveShareTask = task({ drop: true }, async () => {
    return this.shareChangeset.save()
  })

  @action
  private async saveShare() {
    try {
      await this.saveShareTask.perform()
      this.flashQueue.addSuccess({
        subtitle: this.intl.t('pages.bookLibrary.shares.byCollection.edit.saveShare.success'),
      })
      // Redirect to Past tab if share is now expired.
      if (this.shareChangeset.status === ShareStatus.Expired) {
        void this.router.replaceWith('subscription-type.book-library.shares.index.by-collection', {
          queryParams: { status: [ShareStatus.Expired] },
        })
      }
    } catch (e: any) {
      if (didCancel(e)) return
      this.flashQueue.addFail({ subtitle: this.intl.t('pages.bookLibrary.shares.byCollection.edit.saveShare.fail') })
      this.log.error(e)
    }
  }

  @action
  onDateSelect({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) {
    this.shareChangeset.set('startsAt', startDate)
    this.shareChangeset.set('expiresAt', endDate)
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    await this.saveShare()
  }

  @action
  onReset(event: Event) {
    event.preventDefault()
    this.shareChangeset.rollback()
  }

  @action
  expireShareModal(value: boolean) {
    this.showExpireConfirmModal = value
  }

  @action
  async expireShareConfirm() {
    this.shareChangeset.rollback()
    this.shareChangeset.expiresAt = new Date()

    await this.saveShare()

    this.expireShareModal(false)
  }
  get changesetStartsAtDate() {
    return this.shareChangeset.get('startsAt')
  }

  get changesetEndsAtDate() {
    return this.shareChangeset.get('expiresAt')
  }

  get changesetCollectionName() {
    return this.shareChangeset.get('collection')?.name
  }

  <template>
    <form
      data-test-edit-share
      class="space-y-4 pl-4"
      {{on "submit" this.onSubmit}}
      {{on "reset" this.onReset}}
      ...attributes
    >
      <div class="font-semibold">{{t "pages.bookLibrary.shares.byCollection.edit.title"}}</div>
      <BookLibraryDateRange
        @startDateDisabled={{this.editStartDateDisabled}}
        @startDateDisabledText={{t "pages.bookLibrary.shares.byCollection.edit.startDateDisabled"}}
        @startDate={{this.changesetStartsAtDate}}
        @endDate={{this.changesetEndsAtDate}}
        @onDateSelect={{this.onDateSelect}}
        @themeKey={{this.themeKey}}
      />
      <div class="grid grid-cols-[repeat(3,minmax(auto,1fr))] gap-2">
        <div class="justify-self-start">
          <UiButton
            data-test-edit-share-expire
            class="whitespace-nowrap"
            type="button"
            disabled={{this.saveShareTask.isRunning}}
            @appearance={{ButtonAppearance.muted}}
            {{on "click" (fn this.expireShareModal true)}}
          >
            <PhosphorIcon @icon="phosphor-calendar-x-fill" class="text-oceany-blue-300 mr-2" />
            {{t "pages.bookLibrary.shares.byCollection.edit.expireShare"}}
          </UiButton>
        </div>
        <div class="flex justify-center gap-2">
          <UiButton
            data-test-edit-share-reset
            type="reset"
            disabled={{or this.saveShareTask.isRunning this.shareChangeset.isPristine}}
            @appearance={{ButtonAppearance.muted}}
          >
            {{t "cancel"}}
          </UiButton>
          <UiButton
            data-test-edit-share-submit
            type="submit"
            disabled={{or this.saveShareTask.isRunning this.shareChangeset.isPristine this.shareChangeset.isInvalid}}
            @appearance={{ButtonAppearance.regular}}
          >
            {{t "saveChanges"}}
          </UiButton>
        </div>
      </div>
    </form>
    <LayoutBlocksConfirmModal
      data-test-expire-modal
      @title={{t "pages.bookLibrary.shares.byCollection.edit.expireShareModal.title"}}
      @showModal={{this.showExpireConfirmModal}}
      @cancel={{fn this.expireShareModal false}}
      @confirm={{this.expireShareConfirm}}
      @cancelButtonText={{t "cancel"}}
      @confirmButtonText={{t "confirm"}}
      @isSubmitting={{this.saveShareTask.isRunning}}
    >
      <p class="mb-3">
        {{t
          "pages.bookLibrary.shares.byCollection.edit.expireShareModal.youAreAboutTo"
          collectionName=this.changesetCollectionName
          htmlSafe=true
        }}
      </p>
      <p class="mb-3">
        {{t "pages.bookLibrary.shares.byCollection.edit.expireShareModal.thisWillRemoveIt"}}
      </p>
      <p class="mb-3 font-semibold">
        {{t "pages.bookLibrary.shares.byCollection.edit.expireShareModal.cannotBeUndone"}}
      </p>
    </LayoutBlocksConfirmModal>
  </template>
}

export default SharesByCollectionEdit
